import { createReducer } from "typesafe-actions";

import { EntityType, EntityTypesSupportingSavedFilters } from "@mapmycustomers/shared/types/entity";
import SavedFilter from "@mapmycustomers/shared/types/viewModel/SavedFilter";
import { nameComparator } from "@mapmycustomers/shared/util/comparator";

import {
  Actions,
  addSavedFilterToList,
  bulkDeleteSavedFilters,
  createSavedFilter,
  fetchSavedFilters,
  selectSavedFilter,
  updateSavedFilter,
} from "./actions";

type SavedFiltersState = Record<
  EntityTypesSupportingSavedFilters,
  { list: SavedFilter[]; loading: boolean; selectedFilter?: SavedFilter }
> & {
  createLoading: boolean;
  deleteLoading: boolean;
  updateLoading: boolean;
};

const initialState: SavedFiltersState = {
  createLoading: false,
  deleteLoading: false,
  [EntityType.ACTIVITY]: { list: [], loading: false },
  [EntityType.COMPANY]: { list: [], loading: false },
  [EntityType.DEAL]: { list: [], loading: false },
  [EntityType.PERSON]: { list: [], loading: false },
  [EntityType.PIN]: { list: [], loading: false },
  updateLoading: false,
};

const savedFilters = createReducer<SavedFiltersState, Actions>(initialState)
  .handleAction(createSavedFilter.request, (state) => ({
    ...state,
    createLoading: true,
  }))
  .handleAction(createSavedFilter.success, (state) => ({
    ...state,
    createLoading: false,
  }))
  .handleAction(createSavedFilter.failure, (state) => ({
    ...state,
    createLoading: false,
  }))
  .handleAction(updateSavedFilter.request, (state) => ({
    ...state,
    updateLoading: true,
  }))
  .handleAction(updateSavedFilter.success, (state) => ({
    ...state,
    updateLoading: false,
  }))
  .handleAction(updateSavedFilter.failure, (state) => ({
    ...state,
    updateLoading: false,
  }))
  .handleAction(fetchSavedFilters.request, (state, { payload }) => ({
    ...state,
    [payload.entityType]: { ...state[payload.entityType], loading: true },
  }))
  .handleAction(fetchSavedFilters.success, (state, { payload: { entityType, savedFilters } }) => {
    const result = {
      ...state,
      [entityType]: {
        ...state[entityType],
        list: savedFilters,
        loading: false,
        selectedFilter:
          savedFilters.find(({ id }) => id === state[entityType].selectedFilter?.id) ??
          state[entityType].selectedFilter,
      },
    };

    if (entityType === EntityType.PIN) {
      ([EntityType.COMPANY, EntityType.PERSON, EntityType.DEAL] as const).forEach((entityType) => {
        const selectedFilter = state[entityType].selectedFilter
          ? savedFilters.find(({ id }) => id === state[entityType].selectedFilter!.id)
          : undefined;
        if (selectedFilter) {
          result[entityType] = { ...result[entityType], selectedFilter };
        }
      });
    }

    return result;
  })
  .handleAction(fetchSavedFilters.failure, (state, { payload }) => ({
    ...state,
    [payload]: { ...state[payload], list: [], loading: false },
  }))
  .handleAction(addSavedFilterToList, (state, { payload: { entityType, savedFilter } }) => ({
    ...state,
    [entityType]: {
      ...state[entityType],
      list: [...state[entityType].list.filter(({ id }) => id !== savedFilter.id), savedFilter].sort(
        nameComparator
      ),
    },
  }))
  .handleAction(selectSavedFilter, (state, { payload: { entityType, savedFilter } }) => ({
    ...state,
    [entityType]: { ...state[entityType], selectedFilter: savedFilter },
  }))
  .handleAction(bulkDeleteSavedFilters.request, (state) => ({
    ...state,
    deleteLoading: true,
  }))
  .handleAction(bulkDeleteSavedFilters.success, (state, { payload }) => {
    const entityType =
      payload.entityType === EntityType.ACTIVITY ? payload.entityType : EntityType.PIN;
    return {
      ...state,
      deleteLoading: false,
      [entityType]: {
        ...state[entityType],
        list: state[entityType].list.filter(({ id }) => !payload.savedFilterIds.includes(id)),
      },
    };
  })
  .handleAction(bulkDeleteSavedFilters.failure, (state) => ({
    ...state,
    deleteLoading: false,
  }));

export * from "./selectors";
export type SavedFiltersActions = Actions;
export default savedFilters;
